import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../../config/api';
import API_CONFIG from '../../config/api';

// Async thunk to fetch onboarding tree data
export const fetchOnboardingTree = createAsyncThunk(
    'configuration/fetchOnboardingTree',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(
                API_CONFIG.ENDPOINTS.ONBOARDING_TREE
            );
            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch onboarding tree';
            return rejectWithValue(message);
        }
    }
);

// ==================== ALERT RULES THUNKS ====================

// Fetch all alert rules
export const fetchAlertRules = createAsyncThunk(
    'configuration/fetchAlertRules',
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (filters.workspaceId) params.append('workspaceId', filters.workspaceId);
            if (filters.enabled !== undefined) params.append('enabled', filters.enabled);
            if (filters.page) params.append('page', filters.page);
            if (filters.limit) params.append('limit', filters.limit);

            const queryString = params.toString();
            const url = queryString 
                ? `${API_CONFIG.ENDPOINTS.ALERT_RULES}?${queryString}`
                : API_CONFIG.ENDPOINTS.ALERT_RULES;

            const response = await apiClient.get(url);
            
            if (response.data.success) {
                return response.data.data;
            }
            return rejectWithValue(response.data.error?.message || 'Failed to fetch alert rules');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch alert rules';
            return rejectWithValue(message);
        }
    }
);

// Fetch predefined conditions for dropdown
export const fetchAlertConditions = createAsyncThunk(
    'configuration/fetchAlertConditions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_CONFIG.ENDPOINTS.ALERT_RULES_CONDITIONS);
            
            if (Array.isArray(response.data)) {
                return response.data;
            }
            if (response.data?.success && Array.isArray(response.data.data)) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.error?.message || 'Failed to fetch conditions');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch alert conditions';
            return rejectWithValue(message);
        }
    }
);

// Fetch workspaces for dropdown
export const fetchAlertWorkspaces = createAsyncThunk(
    'configuration/fetchAlertWorkspaces',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(API_CONFIG.ENDPOINTS.ALERT_RULES_WORKSPACES);
            
            if (Array.isArray(response.data)) {
                return response.data;
            }
            if (response.data?.success && Array.isArray(response.data.data)) {
                return response.data.data;
            }
            return rejectWithValue(response.data?.error?.message || 'Failed to fetch workspaces');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to fetch workspaces';
            return rejectWithValue(message);
        }
    }
);

// Create a new alert rule
export const createAlertRule = createAsyncThunk(
    'configuration/createAlertRule',
    async (ruleData, { rejectWithValue }) => {
        try {
            const response = await apiClient.post(API_CONFIG.ENDPOINTS.ALERT_RULES, {
                name: ruleData.name,
                description: ruleData.description,
                enabled: ruleData.enabled ?? true,
                conditions: ruleData.conditions,
                emails: ruleData.emails || [],
                workspaceIds: ruleData.workspaceIds || [],
            });
            
            if (response.data.success) {
                return response.data.data;
            }
            return rejectWithValue(response.data.error?.message || 'Failed to create alert rule');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to create alert rule';
            return rejectWithValue(message);
        }
    }
);

// Update an existing alert rule
export const updateAlertRule = createAsyncThunk(
    'configuration/updateAlertRule',
    async ({ ruleId, updates }, { rejectWithValue }) => {
        try {
            const response = await apiClient.put(
                API_CONFIG.ENDPOINTS.ALERT_RULE(ruleId),
                updates
            );
            
            if (response.data.success) {
                return response.data.data;
            }
            return rejectWithValue(response.data.error?.message || 'Failed to update alert rule');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to update alert rule';
            return rejectWithValue(message);
        }
    }
);

// Delete an alert rule
export const deleteAlertRule = createAsyncThunk(
    'configuration/deleteAlertRule',
    async (ruleId, { rejectWithValue }) => {
        try {
            const response = await apiClient.delete(
                API_CONFIG.ENDPOINTS.ALERT_RULE(ruleId)
            );
            
            if (response.data.success) {
                return ruleId; // Return the deleted rule ID
            }
            return rejectWithValue(response.data.error?.message || 'Failed to delete alert rule');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to delete alert rule';
            return rejectWithValue(message);
        }
    }
);

// Toggle alert rule enabled/disabled
export const toggleAlertRule = createAsyncThunk(
    'configuration/toggleAlertRule',
    async ({ ruleId, enabled }, { rejectWithValue }) => {
        try {
            const response = await apiClient.patch(
                API_CONFIG.ENDPOINTS.ALERT_RULE_TOGGLE(ruleId),
                { enabled }
            );
            
            if (response.data.success) {
                return response.data.data;
            }
            return rejectWithValue(response.data.error?.message || 'Failed to toggle alert rule');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to toggle alert rule';
            return rejectWithValue(message);
        }
    }
);

// Reorder alert rules (after drag & drop)
export const reorderAlertRules = createAsyncThunk(
    'configuration/reorderAlertRules',
    async (orderedRules, { rejectWithValue }) => {
        try {
            const response = await apiClient.put(
                API_CONFIG.ENDPOINTS.ALERT_RULES_REORDER,
                {
                    ruleOrder: orderedRules.map((rule, index) => ({
                        id: rule.id,
                        priority: index + 1,
                    })),
                }
            );
            
            if (response.data.success) {
                return orderedRules; // Return the new order
            }
            return rejectWithValue(response.data.error?.message || 'Failed to reorder alert rules');
        } catch (error) {
            const message =
                error.response?.data?.error?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to reorder alert rules';
            return rejectWithValue(message);
        }
    }
);

const initialState = {
    // Onboarding tree data
    onboardingTree: [],
    onboardingTreeStatus: 'idle',
    onboardingTreeError: null,

    // Alert Rules
    alertRules: [],
    alertRulesStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    alertRulesError: null,
    alertRulesPagination: null,

    // Alert Conditions (for dropdown)
    alertConditions: [],
    alertConditionsStatus: 'idle',
    alertConditionsError: null,

    // Alert Workspaces (for dropdown)
    alertWorkspaces: [],
    alertWorkspacesStatus: 'idle',
    alertWorkspacesError: null,

    // Individual operation states
    createRuleStatus: 'idle',
    createRuleError: null,
    updateRuleStatus: 'idle',
    updateRuleError: null,
    deleteRuleStatus: 'idle',
    deleteRuleError: null,
    toggleRuleStatus: 'idle',
    toggleRuleError: null,
    reorderRulesStatus: 'idle',
    reorderRulesError: null,
};

const configurationSlice = createSlice({
    name: 'configuration',
    initialState,
    reducers: {
        clearOnboardingTreeError: (state) => {
            state.onboardingTreeError = null;
        },
        clearAlertRulesError: (state) => {
            state.alertRulesError = null;
            state.createRuleError = null;
            state.updateRuleError = null;
            state.deleteRuleError = null;
            state.toggleRuleError = null;
            state.reorderRulesError = null;
        },
        // Optimistic update for local reorder (before API call)
        setAlertRulesOrder: (state, action) => {
            state.alertRules = action.payload;
        },
        // Optimistic toggle update
        optimisticToggleRule: (state, action) => {
            const { ruleId, enabled } = action.payload;
            const rule = state.alertRules.find(r => r.id === ruleId);
            if (rule) {
                rule.enabled = enabled;
            }
        },
        resetOperationStatus: (state) => {
            state.createRuleStatus = 'idle';
            state.updateRuleStatus = 'idle';
            state.deleteRuleStatus = 'idle';
            state.toggleRuleStatus = 'idle';
            state.reorderRulesStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Onboarding Tree
            .addCase(fetchOnboardingTree.pending, (state) => {
                state.onboardingTreeStatus = 'loading';
                state.onboardingTreeError = null;
            })
            .addCase(fetchOnboardingTree.fulfilled, (state, action) => {
                state.onboardingTreeStatus = 'succeeded';
                state.onboardingTree = action.payload || [];
            })
            .addCase(fetchOnboardingTree.rejected, (state, action) => {
                state.onboardingTreeStatus = 'failed';
                state.onboardingTreeError = action.payload || 'Failed to fetch onboarding tree';
            })

            // Fetch Alert Rules
            .addCase(fetchAlertRules.pending, (state) => {
                state.alertRulesStatus = 'loading';
                state.alertRulesError = null;
            })
            .addCase(fetchAlertRules.fulfilled, (state, action) => {
                state.alertRulesStatus = 'succeeded';
                state.alertRules = action.payload.rules || [];
                state.alertRulesPagination = action.payload.pagination || null;
            })
            .addCase(fetchAlertRules.rejected, (state, action) => {
                state.alertRulesStatus = 'failed';
                state.alertRulesError = action.payload || 'Failed to fetch alert rules';
            })

            // Fetch Alert Conditions
            .addCase(fetchAlertConditions.pending, (state) => {
                state.alertConditionsStatus = 'loading';
                state.alertConditionsError = null;
            })
            .addCase(fetchAlertConditions.fulfilled, (state, action) => {
                state.alertConditionsStatus = 'succeeded';
                state.alertConditions = action.payload || [];
            })
            .addCase(fetchAlertConditions.rejected, (state, action) => {
                state.alertConditionsStatus = 'failed';
                state.alertConditionsError = action.payload || 'Failed to fetch conditions';
            })

            // Fetch Alert Workspaces
            .addCase(fetchAlertWorkspaces.pending, (state) => {
                state.alertWorkspacesStatus = 'loading';
                state.alertWorkspacesError = null;
            })
            .addCase(fetchAlertWorkspaces.fulfilled, (state, action) => {
                state.alertWorkspacesStatus = 'succeeded';
                state.alertWorkspaces = action.payload || [];
            })
            .addCase(fetchAlertWorkspaces.rejected, (state, action) => {
                state.alertWorkspacesStatus = 'failed';
                state.alertWorkspacesError = action.payload || 'Failed to fetch workspaces';
            })

            // Create Alert Rule
            .addCase(createAlertRule.pending, (state) => {
                state.createRuleStatus = 'loading';
                state.createRuleError = null;
            })
            .addCase(createAlertRule.fulfilled, (state, action) => {
                state.createRuleStatus = 'succeeded';
                state.alertRules.push(action.payload);
            })
            .addCase(createAlertRule.rejected, (state, action) => {
                state.createRuleStatus = 'failed';
                state.createRuleError = action.payload || 'Failed to create alert rule';
            })

            // Update Alert Rule
            .addCase(updateAlertRule.pending, (state) => {
                state.updateRuleStatus = 'loading';
                state.updateRuleError = null;
            })
            .addCase(updateAlertRule.fulfilled, (state, action) => {
                state.updateRuleStatus = 'succeeded';
                const index = state.alertRules.findIndex(r => r.id === action.payload.id);
                if (index !== -1) {
                    state.alertRules[index] = action.payload;
                }
            })
            .addCase(updateAlertRule.rejected, (state, action) => {
                state.updateRuleStatus = 'failed';
                state.updateRuleError = action.payload || 'Failed to update alert rule';
            })

            // Delete Alert Rule
            .addCase(deleteAlertRule.pending, (state) => {
                state.deleteRuleStatus = 'loading';
                state.deleteRuleError = null;
            })
            .addCase(deleteAlertRule.fulfilled, (state, action) => {
                state.deleteRuleStatus = 'succeeded';
                state.alertRules = state.alertRules.filter(r => r.id !== action.payload);
            })
            .addCase(deleteAlertRule.rejected, (state, action) => {
                state.deleteRuleStatus = 'failed';
                state.deleteRuleError = action.payload || 'Failed to delete alert rule';
            })

            // Toggle Alert Rule
            .addCase(toggleAlertRule.pending, (state) => {
                state.toggleRuleStatus = 'loading';
                state.toggleRuleError = null;
            })
            .addCase(toggleAlertRule.fulfilled, (state, action) => {
                state.toggleRuleStatus = 'succeeded';
                const rule = state.alertRules.find(r => r.id === action.payload.id);
                if (rule) {
                    rule.enabled = action.payload.enabled;
                    rule.updatedAt = action.payload.updatedAt;
                }
            })
            .addCase(toggleAlertRule.rejected, (state, action) => {
                state.toggleRuleStatus = 'failed';
                state.toggleRuleError = action.payload || 'Failed to toggle alert rule';
            })

            // Reorder Alert Rules
            .addCase(reorderAlertRules.pending, (state) => {
                state.reorderRulesStatus = 'loading';
                state.reorderRulesError = null;
            })
            .addCase(reorderAlertRules.fulfilled, (state, action) => {
                state.reorderRulesStatus = 'succeeded';
                // Update priorities based on new order
                state.alertRules = action.payload.map((rule, index) => ({
                    ...state.alertRules.find(r => r.id === rule.id),
                    priority: index + 1,
                }));
            })
            .addCase(reorderAlertRules.rejected, (state, action) => {
                state.reorderRulesStatus = 'failed';
                state.reorderRulesError = action.payload || 'Failed to reorder alert rules';
            });
    }
});

export const { 
    clearOnboardingTreeError, 
    clearAlertRulesError,
    setAlertRulesOrder,
    optimisticToggleRule,
    resetOperationStatus,
} = configurationSlice.actions;

export default configurationSlice.reducer;
